import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable, Injector, inject, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { environment } from '@environment';
import { AlertService } from '@modules/alert/services/alert.service';
import { FormChangesModalsService } from '@modules/common/services/form-changes-modals.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { relativeDateToWorkDayStart } from '@modules/common/utils/date';
import { AccessLimitGateService } from '@modules/settings/services/access-limit-gate.service';
import type { FormFolderSummaryDto } from '@sharedlib/dtos/dto/forms-app/form.dto';
import type {
  FormCollectionOperation,
  FormCollectionOperationResultDTO,
} from '@sharedlib/dtos/dto/forms-app/form-collection-operation.dto';
import { firstValueFrom, of } from 'rxjs';
import { FormsApiService, type NativeForm } from './forms-api.service';
import { FormsWorkspaceStore } from './forms-workspace.store';

export type FormsCollectionOperation = FormCollectionOperation;
type CollectionResult = FormCollectionOperationResultDTO;
interface PendingSidebarAction {
  readonly tenant: string;
  readonly generation: number;
  readonly commandUid: string;
  readonly label: string;
  readonly createdAt: number;
  readonly operation?: FormsCollectionOperation;
  readonly kind?: 'form' | 'folder';
  readonly title?: string;
  readonly view?: string;
  readonly state?: Readonly<Record<string, unknown>>;
  patchUid?: string;
  made?: NativeForm | FormFolderSummaryDto;
  unanswered: boolean;
  patchUnanswered?: boolean;
  refreshBeforePatch?: boolean;
  task?: Promise<NativeForm | FormFolderSummaryDto | CollectionResult>;
}
/** Boards' rail actions, with server-owned collection snapshots and retained Forms request receipts. One owner per Layout. */
@Injectable()
export class FormsSidebarActionsService {
  private readonly api = inject(FormsApiService);
  private readonly http = inject(HttpClient);
  private readonly store = inject(FormsWorkspaceStore);
  private readonly space = inject(SpaceContextStore);
  private readonly changes = inject(FormChangesModalsService);
  private readonly alert = inject(AlertService);
  private readonly injector = inject(Injector);
  private readonly zone = inject(DisplayTimeZoneService);
  private readonly destroy = inject(DestroyRef);
  private readonly guardKey = 'forms-sidebar-action-' + crypto.randomUUID();
  private readonly guardForm = new FormGroup({ pending: new FormControl('') });
  private pending: PendingSidebarAction | undefined;
  public readonly pendingLabel = signal('');
  public readonly busy = signal(false);
  public constructor() {
    this.changes.registerForm(this.guardKey, this.guardForm, undefined, undefined, { canLeave: () => this.canLeave() });
    const unregister = this.space.registerBeforeContextChange?.(() => of(this.canLeave()));
    const beforeUnload = (event: BeforeUnloadEvent): void => {
      if (!this.canLeave()) event.preventDefault();
    };
    window.addEventListener('beforeunload', beforeUnload);
    this.destroy.onDestroy(() => {
      unregister?.();
      this.changes.unregisterForm(this.guardKey);
      window.removeEventListener('beforeunload', beforeUnload);
    });
  }
  public canLeave(): boolean {
    return !this.pending;
  }
  public confirm(operation: FormsCollectionOperation): void {
    const tenant = this.space.contextId(),
      generation = this.space.contextChanges();
    const perform = (): void => {
      if (this.space.contextId() === tenant && this.space.contextChanges() === generation)
        void this.runCollection(operation).catch((error) => this.store.fail(error));
    };
    if (operation === 'trash-all-archived')
      this.alert.show({
        title: 'Delete item(s)',
        body: 'Every archived form will be moved to Trash.',
        rightButtons: [
          { title: 'CANCEL', close: true },
          { title: 'DELETE ALL', close: true, click: perform },
        ],
      });
    else if (operation === 'empty-trash')
      this.alert.show({
        title: 'Empty Trash',
        body: 'Every form in Trash will be permanently deleted. This can’t be undone.',
        rightButtons: [
          { title: 'CANCEL', close: true },
          { title: 'EMPTY TRASH', close: true, click: perform },
        ],
      });
    else perform();
  }
  public changeFolderState(folder: FormFolderSummaryDto, action: 'archive' | 'trash', apply: (changes: Record<string, unknown>) => void): void {
    this.alert.show({
      title: action === 'archive' ? 'Archive folder' : 'Trash folder',
      body: `${action === 'archive' ? 'Archive' : 'Move to Trash'} “${folder.title}” and its forms? Other apps are unaffected. Restore returns affected forms to their previous state.`,
      rightButtons: [
        { title: 'Cancel', close: true },
        { title: action === 'archive' ? 'Archive' : 'Trash', close: true,
          click: () => apply(action === 'archive' ? { archived: true } : { deleted: true }) },
      ],
    });
  }
  public async runCollection(operation: FormsCollectionOperation): Promise<CollectionResult> {
    const labels: Record<FormsCollectionOperation, string> = {
      'unflag-all': 'Unflag all',
      'remove-all-follow-ups': 'Remove all follow-ups',
      'unsnooze-all': 'Unsnooze all',
      'trash-all-archived': 'Delete all archived forms',
      'empty-trash': 'Empty trash',
    };
    const pending = await this.prepare({ operation, label: labels[operation] });
    const result = await this.settle(pending);
    if (!('matchedCount' in result)) throw Error('The collection result was incomplete.');
    return result;
  }
  public create(kind: 'form', title: string, view: string): Promise<NativeForm>;
  public create(kind: 'folder', title: string, view: string): Promise<FormFolderSummaryDto>;
  public async create(
    kind: 'form' | 'folder',
    title: string,
    view: string,
  ): Promise<NativeForm | FormFolderSummaryDto> {
    if (!this.pending && this.injector.get(AccessLimitGateService).blocks('createItem', this.injector))
      throw Error('This item was not created.');
    const pending = await this.prepare({ kind, title, view, label: 'Create ' + kind, state: this.rowState(view) });
    const result = await this.settle(pending);
    if (!('id' in result)) throw Error('The creation result was incomplete.');
    return result;
  }
  public async applyRowState(item: NativeForm, view: string): Promise<NativeForm> {
    const state = this.rowState(view);
    if (!Object.keys(state).length) return item;
    const pending = await this.prepare({ kind: 'form', view, label: 'Update form', state });
    pending.made = item;
    const result = await this.settle(pending);
    if (!('id' in result)) throw Error('The creation result was incomplete.');
    return result as NativeForm;
  }
  public async retry(): Promise<void> {
    const pending = this.pending;
    if (!pending || this.busy()) return;
    try {
      await this.settle(pending);
    } catch (error) {
      this.store.fail(error);
    }
  }
  private async prepare(
    intent: Pick<PendingSidebarAction, 'label' | 'operation' | 'kind' | 'title' | 'view' | 'state'>,
  ): Promise<PendingSidebarAction> {
    const prior = this.pending,
      tenant = this.space.contextId(),
      generation = this.space.contextChanges();
    if (prior) {
      if (
        prior.tenant !== tenant ||
        prior.operation !== intent.operation ||
        prior.kind !== intent.kind ||
        prior.title !== intent.title ||
        prior.view !== intent.view
      )
        throw Error('Retry the pending Forms action before starting another.');
      return prior;
    }
    if (
      !tenant ||
      !(await this.store.beforeLeave()) ||
      !(await firstValueFrom(this.changes.confirmUnsavedChanges(this.guardKey, true)))
    )
      throw Error('Finish the current changes before this Forms action.');
    if (this.pending) return this.prepare(intent);
    if (this.space.contextId() !== tenant || this.space.contextChanges() !== generation)
      throw Error('The workspace changed before this action.');
    const pending: PendingSidebarAction = {
      ...intent,
      state: intent.state ? structuredClone(intent.state) : undefined,
      tenant,
      generation: this.space.contextChanges(),
      commandUid: crypto.randomUUID(),
      createdAt: Date.now(),
      unanswered: false,
    };
    this.pending = pending;
    this.pendingLabel.set(pending.label);
    return pending;
  }
  private settle(pending: PendingSidebarAction): Promise<NativeForm | FormFolderSummaryDto | CollectionResult> {
    if (pending.task) return pending.task;
    const task = this.perform(pending);
    pending.task = task;
    return task;
  }
  private async perform(pending: PendingSidebarAction): Promise<NativeForm | FormFolderSummaryDto | CollectionResult> {
    this.busy.set(true);
    try {
      if (Date.now() - pending.createdAt > 7 * 24 * 60 * 60 * 1000)
        throw Error(
          'This Forms action acknowledgement expired. Inspect the saved items before deciding on a new operation; its request will not be repeated.',
        );
      let result: NativeForm | FormFolderSummaryDto | CollectionResult;
      if (pending.operation) {
        result = await firstValueFrom(
          this.http.post<CollectionResult>(
            environment.baseUrl + '/api/forms/collection-operations',
            { operation: pending.operation, commandUid: pending.commandUid },
            { context: owningTenantContext(pending.tenant) },
          ),
        );
        if (
          result.commandUid !== pending.commandUid ||
          result.operation !== pending.operation ||
          !Number.isSafeInteger(result.matchedCount) ||
          !Number.isSafeInteger(result.changedCount) ||
          result.changedCount < 0 ||
          result.changedCount !== result.matchedCount ||
          result.matchedCount > 10_000 ||
          !Number.isFinite(Date.parse(result.snapshotAt))
        )
          throw Error('The collection acknowledgement was incomplete. Retry the same action.');
      } else {
        pending.made ??=
          pending.kind === 'folder'
            ? await this.api.createFolder(pending.tenant, pending.title!, pending.commandUid)
            : await this.api.create(pending.tenant, pending.title!, undefined, pending.commandUid);
        if (pending.made.accountId !== pending.tenant)
          throw Error('The creation acknowledgement belongs to another workspace.');
        if (pending.state && Object.keys(pending.state).length) {
          if (pending.refreshBeforePatch) {
            const id = pending.made.id;
            pending.made =
              pending.kind === 'folder'
                ? await this.api.readFolder(pending.tenant, id)
                : await this.api.read(pending.tenant, id);
            if (pending.made.id !== id || pending.made.accountId !== pending.tenant)
              throw Error('The created item belongs to another workspace.');
            pending.patchUid = crypto.randomUUID();
            pending.refreshBeforePatch = false;
          }
          pending.patchUid ??= crypto.randomUUID();
          try {
            if (pending.kind === 'folder') {
              if (!('formsCount' in pending.made)) throw Error('The folder acknowledgement was incomplete.');
              pending.made = await this.api.patchFolder(pending.tenant, pending.made, pending.state, pending.patchUid);
            } else pending.made = await this.api.patch(pending.tenant, pending.made, pending.state, pending.patchUid);
          } catch (error) {
            if (error instanceof HttpErrorResponse && error.status === 409 && !pending.patchUnanswered)
              pending.refreshBeforePatch = true;
            else pending.patchUnanswered = true;
            throw error;
          }
        }
        result = pending.made;
      }
      if (this.pending === pending) {
        this.pending = undefined;
        this.pendingLabel.set('');
      }
      if (
        !this.destroy.destroyed &&
        this.space.contextId() === pending.tenant &&
        this.space.contextChanges() === pending.generation
      )
        void this.store.refresh();
      return result;
    } catch (error) {
      const terminal =
        error instanceof HttpErrorResponse &&
        error.status >= 400 &&
        error.status < 500 &&
        error.status !== 408 &&
        error.status !== 429;
      if (terminal && !pending.unanswered && !pending.made) {
        this.pending = undefined;
        this.pendingLabel.set('');
      } else pending.unanswered = true;
      throw error;
    } finally {
      pending.task = undefined;
      this.busy.set(false);
    }
  }
  private rowState(view: string): Readonly<Record<string, unknown>> {
    if (view === 'flagged') return { flagged: true };
    if (view === 'followup')
      return { followed: relativeDateToWorkDayStart('tomorrow', this.zone.timeZone()).toISOString() };
    if (view === 'snoozed') return { snoozed: new Date(Date.now() + 5 * 60_000).toISOString() };
    return {};
  }
}
